import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ProcessCellForExportParams } from 'ag-grid-enterprise'
import { getLocaleFromLanguage } from './gridFormatUtils'
import {
  createProcessCellForClipboard,
  createProcessCellFromClipboard,
  createProcessDataFromClipboard,
  createProcessDataFromClipboardWithGroupRows,
  GROUP_ROW_SENTINEL
} from './clipboardUtils'

/**
 * Hook that provides a clipboard processor function with locale support
 * Uses the current application language to format numbers correctly
 */
export const useClipboardProcessor = () => {
  const { i18n } = useTranslation()

  return useMemo(() => {
    const locale = getLocaleFromLanguage(i18n.language)
    return createProcessCellForClipboard(locale)
  }, [i18n.language])
}

/**
 * Hook that provides clipboard processors for both copy and paste operations
 */
export const useClipboardProcessors = () => {
  const { i18n } = useTranslation()

  return useMemo(() => {
    const locale = getLocaleFromLanguage(i18n.language)
    return {
      processCellForClipboard: createProcessCellForClipboard(locale),
      processCellFromClipboard: createProcessCellFromClipboard(),
      processDataFromClipboard: createProcessDataFromClipboard()
    }
  }, [i18n.language])
}

/**
 * Hook that provides clipboard processors for grids with group rows that should be skipped.
 * Group rows get the GROUP_ROW_SENTINEL during copy, and are filtered out during paste.
 * Use in grids where group rows are non-editable headers (e.g. AccountMapRuleGrid).
 */
export const useClipboardProcessorsWithGroupRows = () => {
  const { i18n } = useTranslation()

  return useMemo(() => {
    const locale = getLocaleFromLanguage(i18n.language)
    const baseCellProcessor = createProcessCellForClipboard(locale)
    return {
      processCellForClipboard: (params: ProcessCellForExportParams): string | number => {
        if (params.node?.group) return GROUP_ROW_SENTINEL
        return baseCellProcessor(params)
      },
      processDataFromClipboard: createProcessDataFromClipboardWithGroupRows()
    }
  }, [i18n.language])
}
